import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../../contexts/UserContext';

const AdminDashboard = () => {
  const { user } = useUser();
  const [stats, setStats] = useState({
    totalElections: 0,
    totalPositions: 0,
    totalCandidates: 0,
    totalVotes: 0,
  });
  const [recentPositions, setRecentPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizationInfo, setOrganizationInfo] = useState(null);
  const [currentElectionQuota, setCurrentElectionQuota] = useState(null);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchOrganizationInfo();
    fetchEarnings();
    
    // Auto-refresh every 15 seconds to show real-time stats
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchEarnings();
    }, 15000);
    
    // Cleanup on unmount
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch elections with positions and candidates
      const electionsRes = await axios.get(`${process.env.REACT_APP_API_URL}/elections`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => ({ data: { data: [] } }));

      const elections = electionsRes.data.data || [];
      
      // Calculate stats from elections data
      let totalPositions = 0;
      let totalCandidates = 0;
      let totalVotes = 0;
      const allPositions = [];

      elections.forEach(election => {
        if (election.positions) {
          totalPositions += election.positions.length;
          election.positions.forEach(position => {
            allPositions.push({
              ...position,
              electionName: election.name
            });
            if (position.candidates) {
              totalCandidates += position.candidates.length;
              position.candidates.forEach(candidate => {
                // Sum up vote counts - parse to ensure it's a number
                if (candidate.voteCount) {
                  totalVotes += parseInt(candidate.voteCount, 10) || 0;
                }
              });
            }
          });
        }
      });

      // Force new object reference to trigger re-render
      setStats({
        totalElections: elections.length,
        totalPositions: totalPositions,
        totalCandidates: totalCandidates,
        totalVotes: totalVotes,
      });

      // Set recent positions (last 5)
      setRecentPositions([...allPositions.slice(0, 5)]);

      // Calculate active election quota if org requires payment
      if (organizationInfo?.requiresPayment) {
        const activeElections = elections.filter(e => e.status === 'active');
        if (activeElections.length > 0 && activeElections[0].pricingPackage) {
          const election = activeElections[0];
          const pkg = election.pricingPackage;
          
          // Count unique voters in this election
          const positionsInElection = election.positions || [];
          const votersCount = positionsInElection.reduce((count, pos) => {
            return count + (pos.candidates?.reduce((c, cand) => c + (parseInt(cand.voteCount, 10) || 0), 0) || 0);
          }, 0);

          setCurrentElectionQuota({
            electionName: election.name,
            positionsUsed: positionsInElection.length,
            positionsLimit: pkg.maxPositions,
            votersUsed: votersCount,
            votersLimit: pkg.maxVoters,
            packageName: pkg.name,
          });
        }
      }

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (user?.organizationId) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/organizations/${user.organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrganizationInfo(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch organization info:', error);
    }
  };

  const fetchEarnings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/organizations/my-earnings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEarnings(response.data.data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    }
  };

  const formatAmount = (amount, currency = 'UGX') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'UGX',
    }).format(amount);
  };

  const statCards = [
    {
      title: 'Total Elections',
      value: stats.totalElections,
      icon: 'bi-calendar-event',
      color: '#667eea',
      link: '/admin/elections',
    },
    {
      title: 'Total Positions',
      value: stats.totalPositions,
      icon: 'bi-trophy',
      color: '#8b5cf6',
      link: '/admin/positions',
    },
    {
      title: 'Total Candidates',
      value: stats.totalCandidates,
      icon: 'bi-people',
      color: '#10b981',
    },
    {
      title: 'Total Votes',
      value: stats.totalVotes,
      icon: 'bi-check2-square',
      color: '#f59e0b',
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Welcome Header */}
      <div className="mb-4">
        <h1 className="h3 mb-1" style={{ fontWeight: '600' }}>Welcome back, {user?.fullName}!</h1>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Here's what's happening with your elections today
        </p>
      </div>

      {/* Package Quota Display for Paid Organizations */}
      {organizationInfo?.requiresPayment && currentElectionQuota && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card" style={{ border: 'none', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <div className="card-body text-white">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1" style={{ fontWeight: '600' }}>{currentElectionQuota.packageName} Package</h5>
                    <p className="mb-0" style={{ fontSize: '0.875rem', opacity: 0.9 }}>Active election: {currentElectionQuota.electionName}</p>
                  </div>
                  <span className="badge bg-white text-primary" style={{ fontSize: '0.75rem', fontWeight: '600' }}>ACTIVE</span>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.5rem' }}>POSITIONS USED</div>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <div className="progress" style={{ height: '8px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                          <div
                            className="progress-bar bg-white"
                            style={{
                              width: `${currentElectionQuota.positionsLimit ? (currentElectionQuota.positionsUsed / currentElectionQuota.positionsLimit * 100) : 0}%`
                            }}
                          ></div>
                        </div>
                        <div className="mt-2" style={{ fontSize: '0.875rem' }}>
                          {currentElectionQuota.positionsUsed} / {currentElectionQuota.positionsLimit || 'Unlimited'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.5rem' }}>VOTERS USED</div>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <div className="progress" style={{ height: '8px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                          <div
                            className="progress-bar bg-white"
                            style={{
                              width: `${currentElectionQuota.votersLimit ? (currentElectionQuota.votersUsed / currentElectionQuota.votersLimit * 100) : 0}%`
                            }}
                          ></div>
                        </div>
                        <div className="mt-2" style={{ fontSize: '0.875rem' }}>
                          {currentElectionQuota.votersUsed} / {currentElectionQuota.votersLimit || 'Unlimited'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {((currentElectionQuota.positionsLimit && currentElectionQuota.positionsUsed >= currentElectionQuota.positionsLimit * 0.8) ||
                  (currentElectionQuota.votersLimit && currentElectionQuota.votersUsed >= currentElectionQuota.votersLimit * 0.8)) && (
                  <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    You're approaching your package limits. Consider upgrading for more capacity.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Earnings Card - Show if earnings data available */}
      {earnings && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card" style={{ border: 'none', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
              <div className="card-body text-white">
                <div className="row align-items-center">
                  <div className="col-md-3 mb-3 mb-md-0">
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Total Earned</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                      {formatAmount(earnings.earnings?.voteRevenue || 0, earnings.currency)}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '0.25rem' }}>
                      From {earnings.earnings?.voteCount || 0} votes
                    </div>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0">
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Paid Out</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                      {formatAmount(earnings.payouts?.totalPaidOut || 0, earnings.currency)}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '0.25rem' }}>
                      Fees: {formatAmount(earnings.payouts?.totalFees || 0, earnings.currency)}
                    </div>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0">
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Outstanding</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                      {formatAmount(earnings.payouts?.outstanding || 0, earnings.currency)}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '0.25rem' }}>
                      Pending payout
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center">
                      <a href="/admin/payouts" className="btn btn-light btn-sm" style={{ fontWeight: '500' }}>
                        <i className="bi bi-clock-history me-2"></i>
                        View Payout History
                      </a>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.75rem', opacity: 0.9 }}>Net Received: {formatAmount(earnings.payouts?.totalNetPaid || 0, earnings.currency)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="row mb-4">
        {statCards.map((card, index) => (
          <div key={index} className="col-lg-3 col-md-6 mb-4">
            <div className="card h-100" style={{ border: 'none', borderLeft: `4px solid ${card.color}` }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {card.title}
                    </div>
                    <div className="h3 mb-0 mt-2" style={{ fontWeight: '700' }}>
                      {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                    </div>
                  </div>
                  <div style={{ fontSize: '2.5rem', color: card.color, opacity: 0.3 }}>
                    <i className={card.icon}></i>
                  </div>
                </div>
                {card.link && (
                  <Link to={card.link} className="small mt-3 d-block" style={{ color: card.color, textDecoration: 'none', fontWeight: '500' }}>
                    View Details <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        {/* Quick Actions */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <Link
                  to="/admin/elections"
                  className="btn btn-outline-primary text-start"
                  style={{ padding: '0.75rem 1rem' }}
                >
                  <i className="bi bi-calendar-event me-2"></i>
                  Manage Elections
                </Link>
                <Link
                  to="/admin/elections"
                  className="btn btn-outline-primary text-start"
                  style={{ padding: '0.75rem 1rem' }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Create New Election
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Positions */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Recent Positions</h5>
              <Link to="/admin/elections" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>
                View All
              </Link>
            </div>
            <div className="card-body">
              {recentPositions.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2 mb-0" style={{ fontSize: '0.875rem' }}>No positions yet</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {recentPositions.map((position) => (
                    <div key={position.id} className="list-group-item border-0 px-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                            {position.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {position.electionName}
                          </div>
                        </div>
                        <span className="badge bg-primary" style={{ fontSize: '0.75rem' }}>
                          {position.candidates?.length || 0} candidate{position.candidates?.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Organization Info */}
      {user?.organizationId && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body" style={{ padding: '1rem 1.5rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-building me-3" style={{ fontSize: '1.5rem', color: '#667eea' }}></i>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Managing Organization</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                      Organization ID: {user.organizationId}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
